// src/components/GenericMeetingEmailEdit.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import {
  ILegalTransactionMeetingEmail,
  legalTransactionMeetingEmailSchema,
} from '../Interfaces/ILegalTransactionMeetingEmail';
import { GenericMeetingEmailFormFields } from './GenericMeetingEmailFormFields';
import type { IDocument } from '../Interfaces/IDocument';

export interface Props {
  open: boolean;
  template: ILegalTransactionMeetingEmail;
  onSave: (data: ILegalTransactionMeetingEmail) => void;
  onCancel: () => void;
  onDelete?: () => void;
}

export function GenericMeetingEmailEdit({
  open,
  template,
  onSave,
  onCancel,
  onDelete,
}: Props) {
  const [form, setForm] = useState<ILegalTransactionMeetingEmail>(
    legalTransactionMeetingEmailSchema.parse(template)
  );

  useEffect(() => {
    setForm(legalTransactionMeetingEmailSchema.parse(template));
  }, [template]);

  const onField = <K extends keyof ILegalTransactionMeetingEmail>(
    key: K,
    value: ILegalTransactionMeetingEmail[K]
  ) => {
    setForm(f => ({ ...f, [key]: value }));
  };

  const handleSave = () => {
    onSave(form);
  };

  // helpers for Documents array
  const docs = form.Documents as IDocument[];
  const addDocs = (newDocs: IDocument[]) => {
    setForm(f => ({ ...f, Documents: [...(f.Documents as IDocument[]), ...newDocs] }));
  };
  const removeDoc = (idx: number) => {
    onField(
      'Documents',
      docs.filter((_, i) => i !== idx)
    );
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth='lg' fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>{form.Title || 'E-Mail bearbeiten'}</Box>
        {onDelete && (
          <IconButton size='small' color='error' onClick={onDelete}>
            <Delete />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent>
        <GenericMeetingEmailFormFields
          form={form}
          onField={onField}
          docs={docs}
          onAddDoc={addDocs}
          onRemoveDoc={removeDoc}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Abbruch</Button>
        <Button variant='contained' onClick={handleSave}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}
